import { API_URL } from '../constants/constants.ts';
import { TAPE_API_URL } from '../config/tapeConfig.ts';

export interface UserPreferences {
  jamieFullAutoEnabled?: boolean;
  autoStartCrosspost?: boolean;
  crosspostSignature?: string;
  scheduledPostSlots?: ScheduledSlot[];
  randomizePostTime?: boolean;
  jamieAssistDefaults?: string; // Jamie Assist preferences (tone, style, hashtags, etc.)
  
  // UI View Preferences
  searchViewStyle?: 'classic' | 'split_screen';
  searchResultViewStyle?: 'list' | 'galaxy';
  preferredAIClipsViewStyle?: 'list' | 'grid';
  showAxisLabels?: boolean; // Display axis labels in galaxy view (default: true for new users)
  autoPlayOnStarClick?: boolean; // Auto-play audio when clicking a star in galaxy view (default: true for new users)
  
  // Admin Settings
  adminFeedId?: string;
  isFirstVisit?: boolean;
  
  // Add other user preferences as needed
  [key: string]: any;
}

export interface ScheduledSlot {
  id: string;
  dayOfWeek: number; // 0-6 (Sunday = 0)
  time: string; // HH:MM format in user's timezone
  enabled: boolean;
}

export interface PreferencesResponse {
  preferences: UserPreferences;
  schemaVersion: number;
}

export interface PreferencesRequest {
  preferences: UserPreferences;
  schemaVersion: number;
}

const CURRENT_SCHEMA_VERSION = 20250812001;

/**
 * Generate default scheduled slots (9:45 AM and 4:45 PM, Monday-Friday)
 */
export function generateDefaultScheduledSlots(): ScheduledSlot[] {
  const slots: ScheduledSlot[] = [];
  
  // Monday through Friday (1-5)
  for (let day = 1; day <= 5; day++) {
    // Morning slot: 9:45 AM
    slots.push({
      id: `default-${day}-morning`,
      dayOfWeek: day,
      time: '09:45',
      enabled: true
    });
    
    // Afternoon slot: 4:45 PM
    slots.push({
      id: `default-${day}-afternoon`,
      dayOfWeek: day,
      time: '16:45',
      enabled: true
    });
  }
  
  return slots;
}

class PreferencesService {
  private static readonly BASE_URL = API_URL;

  /** Resolve the base URL for a single call. Default routes to the
   *  main-app `API_URL`. When the optional `useTapeBackend` flag is
   *  true (only Tape persona traffic sets this, and only while
   *  `TAPE_USING_LOCAL_BACKEND` is on), the call goes to the Tape
   *  backend instead — so a local Tape server can read the persona
   *  it just wrote without round-tripping prod. */
  private static resolveBaseUrl(useTapeBackend?: boolean): string {
    return useTapeBackend ? TAPE_API_URL : this.BASE_URL;
  }

  /**
   * Get user preferences from the backend.
   * @param useTapeBackend Optional. When true, hits `TAPE_API_URL`
   * instead of the main-app `API_URL`. Tape persona reads set this
   * during local-dev so the same backend that writes prefs reads them.
   */
  static async getPreferences(authToken: string, useTapeBackend = false): Promise<PreferencesResponse> {
    try {
      const response = await fetch(`${this.resolveBaseUrl(useTapeBackend)}/api/preferences`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        if (response.status === 404) {
          // No preferences found, return empty preferences
          return {
            preferences: {},
            schemaVersion: CURRENT_SCHEMA_VERSION
          };
        }
        throw new Error(`Failed to fetch preferences: ${response.status}`);
      }

      const data = await response.json();
      return {
        preferences: data.preferences || {},
        schemaVersion: data.schemaVersion || CURRENT_SCHEMA_VERSION
      };
    } catch (error) {
      console.error('Error fetching preferences:', error);
      throw error;
    }
  }

  /**
   * Update user preferences in the backend.
   * @param useTapeBackend Optional. When true, hits `TAPE_API_URL`
   * instead of the main-app `API_URL`. Tape persona writes set this
   * during local-dev so the local Tape backend can read its own writes.
   */
  static async updatePreferences(
    authToken: string,
    preferences: UserPreferences,
    useTapeBackend = false
  ): Promise<PreferencesResponse> {
    try {
      const requestData: PreferencesRequest = {
        preferences,
        schemaVersion: CURRENT_SCHEMA_VERSION
      };

      const response = await fetch(`${this.resolveBaseUrl(useTapeBackend)}/api/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication failed. Please sign in again.');
        }
        throw new Error(`Failed to update preferences: ${response.status}`);
      }

      const data = await response.json();
      return {
        preferences: data.preferences,
        schemaVersion: data.schemaVersion || CURRENT_SCHEMA_VERSION
      };
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  }

  /**
   * Get current schema version
   */
  static getCurrentSchemaVersion(): number {
    return CURRENT_SCHEMA_VERSION;
  }
}

export default PreferencesService;
